import {
  CartBodyProps,
  CartItem,
  DiningSelectionMetadata,
  PrepaidPaymentProps,
} from '@msc-b2c/interfaces/types';
import {
  DiningFlowEntryPoint,
  ErrorManagerCodeEnum,
  OBSTypesEnum,
  PageType,
} from '@msc-b2c/interfaces/enums';
import styles from './cartbody.module.scss';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  formatPrice,
  getActiveSession,
  getFormatPriceCurrencyOrder,
  isLayerMyAreaExcursionOpen,
  managePlurals,
  myAreaShopExcursion,
  myAreaShopOBS,
  replacePlaceholders,
  RootState,
  selectedMyAreaExcursionCard,
  selectedMyAreaOBSCard,
  setErrorManagerCode,
  specialtyRestaurants,
  toggleDisplayOverlay,
  updateCartItems,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { SideSummary } from './SideSummary/SideSummary';
import { Card } from './Card/Card';
import { Drawer } from '../../../drawer/Drawer';
import Passengers from '../../../postbookingresults/components/drawercontent-subcomponents/Passengers';
import ImageTitle from '../../../postbookingresults/components/drawercontent-subcomponents/ImageTitle';
import { getShopExcursionData, getShopOBSData } from '../../../utils/myAreaUtils';
import {
  addDiningItemToCart,
  addToCart,
  getMergedCartDisplayItems,
} from '../../../utils/myAreaCartUtils';
import { Accordion } from '@msc-b2c/shared-components';
import { getPricesCart } from '../../utils/getPricesCart';
import { PrepaidPayment } from '../prepaidpayment/PrepaidPayment';
import { passengersToRecord } from '../../../postbookingresults/utils/PostBookingResultsUtilities';
import { DiningMultistepDrawer } from '../../../postbookingresults/components/drawercontent-subcomponents/flowtypes/DiningMultistepDrawer';
import { useCartDiningSeedContext } from '../../hooks/useCartDiningSeedContext';
import { getRestaurantInfoFromGraphql } from '../../../postbookingresults/components/drawercontent-subcomponents/flowtypes/utils/diningRestaurantInfoFromCF';

export function CartBody(
  props: Readonly<
    CartBodyProps &
      Partial<PrepaidPaymentProps> & {
        onItemDelete?: (itemCode: string, itemDescription: string, startDate?: string) => void;
        isPricesLoading?: boolean;
        pageType?: PageType;
      }
  >
) {
  const { items, onItemDelete, isPricesLoading: isPricesLoadingProp, pageType, ...labels } = props;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState<CartItem | null>(null);
  const [triggerAddToCart, setTriggerAddToCart] = useState(false);
  const [passengersToAdd, setPassengersToAdd] = useState<Record<
    string,
    Record<string, boolean>
  > | null>(null);
  const [diningSelectionToAdd, setDiningSelectionToAdd] = useState<DiningSelectionMetadata | null>(
    null
  );
  const specialityRestaurantsFromCf = useSelector(
    (state: RootState) => state.myAreaCommonConfig.specialtyRestaurants
  );

  const [shopRequestToRestaurants, setShopRequestToRestaurants] = useState<boolean>(false);
  const [initialCheckedPassengers, setInitialCheckedPassengers] = useState<Record<
    string,
    Record<string, boolean>
  > | null>(null);
  const [currentCheckedPassengers, setCurrentCheckedPassengers] = useState<Record<
    string,
    Record<string, boolean>
  > | null>(null);
  const [currentSelectedLanguages, setCurrentSelectedLanguages] = useState<Record<
    string,
    string
  > | null>(null);
  const hasInitialStateBeenSet = useRef(false);
  const [isPricesLoadingLocal, setIsPricesLoadingLocal] = useState(false);
  const isPricesLoading =
    isPricesLoadingProp !== undefined ? isPricesLoadingProp : isPricesLoadingLocal;
  const [isDrawerProcessing, setIsDrawerProcessing] = useState(false);
  const dispatch = useDispatch();

  const bookingNumber = getActiveSession().bookingNumber ?? '';
  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const obs = useSelector((state: RootState) => state.myCruise.obs?.onboardServices || []);
  const excursions = useSelector((state: RootState) => state.myCruise.excursions?.excursions || []);
  const {
    selectedMyAreaExcursionCard: myAreaExcursionCardData,
    selectedMyAreaOBSCard: myAreaOBSCardData,
    isLayerMyAreaExcursionOpen: isLayerExcursionOpen,
  } = useSelector((state: RootState) => state.myAreaDrawers);
  const { prices, items: cartItems } = useSelector((state: RootState) => state.myAreaCart);
  const drawerProps = useSelector((state: RootState) => state.myAreaCommonConfig.drawerProps);
  const diningCartRecapProps = useSelector(
    (state: RootState) => state.myAreaCommonConfig.diningProps.diningCartReservationRecapProps
  );
  const languageList = myAreaExcursionCardData?.activityDetails?.guideLanguageCodes ?? [];

  const cartItemsRef = useRef(cartItems);
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  const isDiningForfaiDrawer =
    !isLayerExcursionOpen && myAreaOBSCardData?.type?.code === OBSTypesEnum.RESTAURANTS;
  const diningSeedContext = useCartDiningSeedContext({
    selectedCartItem,
    bookingData,
  });

  const handleEdit = useCallback(
    (item: CartItem) => {
      let matched;
      if (item.itemType === 'ONBOARD_SERVICE') {
        matched = obs.find((o) => o.code === item.code);
        if (matched) {
          dispatch(selectedMyAreaOBSCard(matched));
          dispatch(isLayerMyAreaExcursionOpen(false));
          setIsDrawerOpen(true);
          // Dining items use already-loaded card data on bootstrap; INT135 fetch happens on demand.
          if (matched.type?.code !== OBSTypesEnum.RESTAURANTS) {
            getShopOBSData(matched).then((res) => {
              dispatch(myAreaShopOBS(res));
            });
          } else {
            setShopRequestToRestaurants(true); // if is FORFAI get graphql
          }
        }
      } else if (item.itemType === 'EXCURSION') {
        matched = excursions.find((e) => e.code === item.code && e.startDate === item.startDate);
        if (matched) {
          const languageRecords = passengersToRecord(item.passengers);
          dispatch(selectedMyAreaExcursionCard(matched));
          dispatch(isLayerMyAreaExcursionOpen(true));
          setCurrentSelectedLanguages(languageRecords);
          setIsDrawerOpen(true);
          getShopExcursionData(matched)
            .then((res) => {
              dispatch(myAreaShopExcursion(res));
            })
            .catch(() => {
              handleCloseDrawer();
              dispatch(setErrorManagerCode(ErrorManagerCodeEnum.NO_EXCURSION_MATCH));
            });
        }
      }
      setSelectedCartItem(item);
    },
    [dispatch, obs, excursions]
  );

  useEffect(() => {
    if (!isDrawerOpen && !!props.editedDiningPackage) {
      props.showEditSuccess?.();
    }
  }, [isDrawerOpen]);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedCartItem(null);
    setInitialCheckedPassengers(null);
    setCurrentCheckedPassengers(null);
    setCurrentSelectedLanguages(null);
    setDiningSelectionToAdd(null);
    dispatch(selectedMyAreaOBSCard(null));
    dispatch(selectedMyAreaExcursionCard(null));
    toggleDisplayOverlay(false);
  }, [dispatch]);

  const arePassengersEqual = (
    passengers1: Record<string, Record<string, boolean>> | null,
    passengers2: Record<string, Record<string, boolean>> | null
  ): boolean => {
    if (!passengers1 && !passengers2) return true;
    if (!passengers1 || !passengers2) return false;

    const keys1 = Object.keys(passengers1);
    const keys2 = Object.keys(passengers2);
    if (keys1.length === 0 && keys2.length === 0) return true;
    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!passengers2[key]) return false;

      const cabin1 = passengers1[key];
      const cabin2 = passengers2[key];
      const cabinKeys1 = Object.keys(cabin1);
      const cabinKeys2 = Object.keys(cabin2);

      if (cabinKeys1.length !== cabinKeys2.length) return false;

      for (const cabinKey of cabinKeys1) {
        if (cabin1[cabinKey] !== cabin2[cabinKey]) return false;
      }
    }

    return true;
  };

  const areLanguagesSelectionFinished = () => {
    return (
      !myAreaExcursionCardData ||
      languageList.length <= 1 ||
      (passengersToAdd &&
        languageList.length &&
        Object.values(passengersToAdd).every((passenger) =>
          Object.entries(passenger)
            .filter(([, isChecked]) => isChecked === true)
            .every(([passengerId]) => {
              const lang = currentSelectedLanguages?.[passengerId];
              return typeof lang === 'string' && lang.trim() !== '';
            })
        ))
    );
  };

  useEffect(() => {
    if (specialityRestaurantsFromCf.length === 0) {
      getRestaurantInfoFromGraphql().then((restaurantItems) => {
        if (restaurantItems) {
          dispatch(specialtyRestaurants(restaurantItems));
        }
      });
    }
  }, []);

  useEffect(() => {
    if (isDrawerOpen) {
      hasInitialStateBeenSet.current = false;
    } else {
      setInitialCheckedPassengers(null);
      setCurrentCheckedPassengers(null);
      setCurrentSelectedLanguages(null);
      hasInitialStateBeenSet.current = false;
    }
  }, [isDrawerOpen]);

  const wasProcessingRef = useRef(false);

  useEffect(() => {
    if (isDrawerProcessing) {
      wasProcessingRef.current = true;
    } else if (wasProcessingRef.current && !isDrawerProcessing && isDrawerOpen) {
      wasProcessingRef.current = false;
      handleCloseDrawer();
    }
  }, [isDrawerProcessing, isDrawerOpen, handleCloseDrawer]);

  const handleCheckedPassengersChange = useCallback(
    (
      checkedPassengers: Record<string, Record<string, boolean>>,
      selectedLanguages?: Record<string, string>
    ) => {
      if (isDrawerOpen && !hasInitialStateBeenSet.current) {
        setInitialCheckedPassengers(checkedPassengers);
        setCurrentCheckedPassengers(checkedPassengers);
        hasInitialStateBeenSet.current = true;
        if (selectedLanguages) {
          setCurrentSelectedLanguages(selectedLanguages);
        }
      } else if (hasInitialStateBeenSet.current) {
        setCurrentCheckedPassengers(checkedPassengers);
        if (selectedLanguages) {
          setCurrentSelectedLanguages(selectedLanguages);
        }
      }
    },
    [isDrawerOpen]
  );

  const isApplyDisabled =
    !isDrawerOpen ||
    initialCheckedPassengers === null ||
    currentCheckedPassengers === null ||
    arePassengersEqual(initialCheckedPassengers, currentCheckedPassengers) ||
    !areLanguagesSelectionFinished();

  const handleItemDelete = useCallback(
    (itemCode: string, itemDescription: string, startDate?: string) => {
      if (onItemDelete) {
        onItemDelete(itemCode, itemDescription, startDate);
      }
    },
    [onItemDelete]
  );

  useEffect(() => {
    if (passengersToAdd && triggerAddToCart) {
      const currentCard = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;

      if (currentCard?.code) {
        const itemCode = currentCard.code;
        const hasSelectedPassengers = Object.values(passengersToAdd).some((cabinPassengers) =>
          Object.values(cabinPassengers).some(Boolean)
        );

        if (hasSelectedPassengers) {
          const itemListName = selectedCartItem?.itemListName;
          const updatedCart: CartItem[] = isDiningForfaiDrawer
            ? addDiningItemToCart(
                myAreaOBSCardData ?? null,
                passengersToAdd,
                diningSelectionToAdd ?? {
                  entryPoint: DiningFlowEntryPoint.CART,
                },
                undefined,
                itemListName
              )
            : addToCart(currentCard, passengersToAdd, undefined, {
                selectedLanguages: currentSelectedLanguages,
                itemListName,
              });
          dispatch(updateCartItems(updatedCart));
          const { bookingNumber: activeBookingNumber = '', cruiseId } = getActiveSession();
          if (cruiseId && activeBookingNumber && updatedCart.length > 0) {
            setIsDrawerProcessing(true);
            setIsPricesLoadingLocal(true);
            getPricesCart(dispatch, cruiseId, activeBookingNumber, false)
              .then(() => {
                setIsPricesLoadingLocal(false);
                setIsDrawerProcessing(false);
              })
              .catch(() => {
                setIsPricesLoadingLocal(false);
                setIsDrawerProcessing(false);
              });
          } else {
            handleCloseDrawer();
          }
        } else {
          const service = isLayerExcursionOpen
            ? excursions.find((e) => e.code === itemCode)
            : obs.find((o) => o.code === itemCode);
          const itemDescription =
            service?.description ||
            currentCard?.description ||
            ('name' in currentCard ? currentCard.name : undefined) ||
            selectedCartItem?.code ||
            itemCode;

          if (onItemDelete) {
            onItemDelete(itemCode, itemDescription, selectedCartItem?.startDate);
            handleCloseDrawer();
          }
        }
      }

      setTriggerAddToCart(false);
      setPassengersToAdd(null);
      setDiningSelectionToAdd(null);
    }
  }, [
    currentSelectedLanguages,
    diningSelectionToAdd,
    passengersToAdd,
    triggerAddToCart,
    isDiningForfaiDrawer,
    isLayerExcursionOpen,
    myAreaExcursionCardData,
    myAreaOBSCardData,
    dispatch,
    handleCloseDrawer,
    obs,
    excursions,
    selectedCartItem,
    onItemDelete,
  ]);

  const handleApply = useCallback(() => {
    setTriggerAddToCart(true);
    toggleDisplayOverlay(false);
  }, []);

  const handleAccordionToggle = useCallback(() => undefined, []);

  if (items.length < 1) return null;

  // Combined adult + minor ALLINC packages count and render as a single card.
  const displayItems = getMergedCartDisplayItems(items);

  const itemsCount = managePlurals(
    displayItems.length,
    replacePlaceholders(labels.itemsCount, {
      count: displayItems.length.toString(),
    })
  );
  const formattedTotalPrice = getFormatPriceCurrencyOrder(
    formatPrice(String(prices?.price?.total?.afterDiscount || 0))
  );

  const getItems = () => (
    <div
      className={
        styles[props.accordionEnabled ? 'cmp-cartbody__accordion-items' : 'cmp-cartbody__cards']
      }
    >
      {props.accordionEnabled && (
        <div className={styles['cmp-cartbody__accordion-cards-summary']}>
          <SideSummary
            items={items}
            labels={props}
            isPricesLoading={isPricesLoading}
            pageType={pageType ?? PageType.CART_PAGE}
          />
        </div>
      )}
      {displayItems.map(({ parent, related }) => (
        <Fragment
          key={`${parent.itemType}-${parent.code}-${
            parent.startDate ?? parent.deliveryDate ?? 'no-date'
          }-${parent.relatedItemCode ?? 'no-related'}`}
        >
          {props.accordionEnabled && <hr className={styles['cmp-cartbody__accordion-separator']} />}
          <Card
            item={parent}
            relatedItems={related}
            labels={labels}
            onEdit={() => handleEdit(parent)}
            onDiningEdit={() => handleEdit(parent)}
            onDelete={handleItemDelete}
            isPricesLoading={isPricesLoading}
          />
        </Fragment>
      ))}
    </div>
  );

  const isCartPage = (pageType ?? PageType.CART_PAGE) === PageType.CART_PAGE;

  return (
    <div
      className={`${styles['cmp-cartbody']} ${
        props.accordionEnabled ? styles['cmp-cartbody__accordion-cards'] : ''
      } ${isCartPage ? styles['cmp-cartbody--cart-page'] : styles['cmp-cartbody--checkout-page']}`}
    >
      {(props.accordionEnabled && (
        <div className={styles['cmp-cartbody__accordion-wrapper']}>
          <Accordion
            styleOption={styles['cmp-cartbody__accordion']}
            question={labels.accordionTitle ?? ''}
            answer={''}
            isOpen={!!labels.openCartAccordionByDefault}
            onToggle={handleAccordionToggle}
            openingIcon={labels.accordionOpenIcon ?? ''}
            openingAriaLabel={labels.accordionOpenIconAriaLabel}
            closingIcon={labels.accordionCloseIcon ?? ''}
            closingAriaLabel={labels.accordionCloseIconAriaLabel}
            multi={true}
            viewDetailsLabel={formattedTotalPrice}
            additionalDetailsLabel={itemsCount}
          >
            {getItems()}
          </Accordion>
          <PrepaidPayment
            paymentMethodsTitle={labels.paymentMethodsTitle}
            paymentSectionTitle={labels.paymentSectionTitle}
            paymentSectionDescription={labels.paymentSectionDescription}
            paymentSectionFreeCartDescription={labels.paymentSectionFreeCartDescription}
            dateFormat={labels.dateFormat}
            cardPaymentDetailsLabel={labels.cardPaymentDetailsLabel}
            cardNumberPlaceholder={labels.cardNumberPlaceholder}
            cardExpirationDatePlaceholder={labels.cardExpirationDatePlaceholder}
            cardCvvPlaceholder={labels.cardCvvPlaceholder}
            cardCvvDisclaimer={labels.cardCvvDisclaimer}
            mandatoryLabel={labels.mandatoryLabel}
            termsAndConditionSection={labels.termsAndConditionSection}
            paymentButtonCTA={labels.paymentButtonCTA}
            datatrans={props.datatrans}
            emptyCartRedirectionLink={props.emptyCartRedirectionLink}
            ecouponErrorMessageConfigList={labels.ecouponErrorMessageConfigList}
            ecouponErrorMessageConfigListResources={labels.ecouponErrorMessageConfigListResources}
          />
        </div>
      )) ||
        getItems()}
      <SideSummary
        items={items}
        labels={labels}
        isPricesLoading={isPricesLoading}
        pageType={pageType ?? PageType.CART_PAGE}
      />
      {isDiningForfaiDrawer ? (
        <DiningMultistepDrawer
          isDrawerOpen={isDrawerOpen}
          booking={bookingData}
          flowConfig={{ entryPoint: DiningFlowEntryPoint.CART }}
          preserveSelectionGoingBack={true}
          cartReservationRecapProps={diningCartRecapProps}
          seedContext={diningSeedContext}
          onClose={handleCloseDrawer}
          onDrawerAbandonConfirmed={() => {
            const { bookingNumber: activeBookingNumber = '', cruiseId } = getActiveSession();
            if (!cruiseId || !activeBookingNumber) {
              return;
            }
            setIsPricesLoadingLocal(true);
            getPricesCart(dispatch, cruiseId, activeBookingNumber, false)
              .then(() => setIsPricesLoadingLocal(false))
              .catch(() => setIsPricesLoadingLocal(false));
          }}
          onConfirmPassengers={(passengers, _currentStep, diningSelection) => {
            setPassengersToAdd(passengers);
            setDiningSelectionToAdd(diningSelection ?? null);
          }}
          onApply={handleApply}
          setIsConfirmationDrawer={() => {
            // Cart edit flow does not render a separate confirmation drawer.
          }}
          setEditedDiningPackage={() =>
            props.setEditedDiningPackage?.(myAreaOBSCardData.description)
          }
        />
      ) : (
        <Drawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          onApply={handleApply}
          enableClosingByClickingOutside={true}
          stylingOption="transparent-header"
          firstButtonLabel={drawerProps.drawerEditLabel}
          processingFirstButtonLabel={drawerProps.updatingStatusLabel}
          processingFirstButtonIcon={drawerProps.updatingStatusLabelIcon}
          secondButtonLabel={''}
          closeIcon={'close'}
          disabled={isApplyDisabled}
          isProcessing={isDrawerProcessing}
        >
          <>
            <ImageTitle
              imageCode={selectedCartItem?.code ?? ''}
              pageType={pageType ?? PageType.CART_PAGE}
            />
            <Passengers
              isDrawerOpen={isDrawerOpen}
              setPassengersToAdd={setPassengersToAdd}
              selectedLanguages={currentSelectedLanguages ?? undefined}
              setSelectedLanguages={setCurrentSelectedLanguages}
              onCheckedPassengersChange={handleCheckedPassengersChange}
              isRestrictedLanguageSelection={true}
            />
          </>
        </Drawer>
      )}
    </div>
  );
}
